// components/table-with-column-manager/summary-generator.tsx
import React from 'react'
import { Table } from 'antd'
import { FiltersRow } from '../styled-elements/filters-row'
import type { FilterConfig } from './types'

interface LeafColumnDescriptor {
    key?: string | number
}

interface GenerateSummaryRowOptions {
    leadingColumnCount: number
    actionsCell?: React.ReactNode
    actionsCellClassName?: string
}

/**
 * Генерирует строку summary с фильтрами, автоматически размещая их по столбцам
 * и заполняя пустые промежутки пустыми ячейками
 */
export function generateSummaryRow(
    visibleColumns: LeafColumnDescriptor[],
    filters: FilterConfig[],
    options: GenerateSummaryRowOptions
): React.ReactElement {
    const { leadingColumnCount, actionsCell, actionsCellClassName } = options

    // Создаем Map для быстрого поиска фильтров по columnKey
    // Если несколько фильтров имеют одинаковый columnKey, используется последний
    const filtersMap = new Map<string, FilterConfig>()
    filters.forEach(filter => {
        if (filter.columnKey) {
            filtersMap.set(filter.columnKey, filter)
        }
    })

    const cells: React.ReactNode[] = []

    // Сначала создаем пустые ячейки для leading columns (rowSelection, expandable)
    for (let i = 0; i < leadingColumnCount; i++) {
        cells.push(<Table.Summary.Cell key={`leading-${i}`} index={i} />)
    }

    // Проходим по всем видимым столбцам данных
    visibleColumns.forEach((column, columnIndex) => {
        const actualIndex = leadingColumnCount + columnIndex
        // Ключ уже должен быть строкой после flattenLeafColumns
        const columnKey = column.key as string | undefined

        // Проверяем, есть ли фильтр для этого столбца
        const filter = columnKey ? filtersMap.get(columnKey) : undefined

        if (filter) {
            // Проверяем условие показа фильтра
            let shouldShow = true
            if (filter.condition !== undefined) {
                if (typeof filter.condition === 'function') {
                    try {
                        shouldShow = filter.condition()
                    } catch (error) {
                        // Если функция condition выбрасывает ошибку, не показываем фильтр
                        shouldShow = false
                    }
                } else {
                    shouldShow = filter.condition
                }
            }

            if (shouldShow) {
                // Вставляем фильтр
                cells.push(
                    <Table.Summary.Cell
                        key={`filter-${columnKey}`}
                        index={actualIndex}
                        className={filter.className}
                    >
                        {filter.content}
                    </Table.Summary.Cell>
                )
            } else {
                // Условие не выполнено - вставляем пустую ячейку
                cells.push(
                    <Table.Summary.Cell
                        key={`empty-${columnKey || columnIndex}`}
                        index={actualIndex}
                    />
                )
            }
        } else {
            // Нет фильтра для этого столбца - вставляем пустую ячейку
            cells.push(
                <Table.Summary.Cell key={`empty-${columnKey || columnIndex}`} index={actualIndex} />
            )
        }
    })

    // Если указана actionsCell, заменяем последнюю ячейку данных на actionsCell
    // Важно: заменяем только если есть видимые столбцы данных (не только leading columns)
    if (actionsCell && visibleColumns.length > 0) {
        const lastIndex = leadingColumnCount + visibleColumns.length - 1
        // Удаляем последнюю ячейку данных (пустую или фильтр)
        // Это безопасно, так как мы проверили visibleColumns.length > 0
        cells.pop()
        // Вставляем actionsCell
        cells.push(
            <Table.Summary.Cell
                key='actions-cell'
                index={lastIndex}
                className={actionsCellClassName || 'actions-cell'}
            >
                {actionsCell}
            </Table.Summary.Cell>
        )
    } else if (actionsCell && visibleColumns.length === 0) {
        // Edge case: если нет видимых столбцов данных, но есть actionsCell,
        // размещаем его в последней leading column ячейке
        const lastLeadingIndex = leadingColumnCount > 0 ? leadingColumnCount - 1 : 0
        // Заменяем последнюю leading column ячейку
        if (cells.length > 0) {
            cells.pop()
        }
        cells.push(
            <Table.Summary.Cell
                key='actions-cell'
                index={lastLeadingIndex}
                className={actionsCellClassName || 'actions-cell'}
            >
                {actionsCell}
            </Table.Summary.Cell>
        )
    }

    return <FiltersRow>{cells}</FiltersRow>
}
